import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import SettingsContentWrapper from "./SettingsContentWrapper";
import {
  teamService,
  TeamMember,
  TeamInvitation,
} from "@/services/teamService";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  UserPlus,
  Mail,
  MoreVertical,
  Clock,
  AlertCircle,
  X,
  Send,
  Loader2,
  RefreshCw,
} from "lucide-react";

function TeamSettingsContent() {
  const { user, organization, userProfile } = useAuth();
  const { toast } = useToast();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [selectedTab, setSelectedTab] = useState("members");
  const [isLoading, setIsLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<
    TeamInvitation[]
  >([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const loadTeamData = useCallback(async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    try {
      const [membersResult, invitationsResult] = await Promise.all([
        teamService.getTeamMembers(organization.id),
        teamService.getPendingInvitations(organization.id),
      ]);

      if (membersResult.error) {
        console.error("Error loading team members:", membersResult.error);
      }
      if (invitationsResult.error) {
        console.error("Error loading invitations:", invitationsResult.error);
      }

      if (membersResult.data) {
        setTeamMembers(membersResult.data);
      } else {
        setTeamMembers([]);
      }

      if (invitationsResult.data) setPendingInvitations(invitationsResult.data);
    } catch (error) {
      console.error("Failed to load team data:", error);
      toast({
        variant: "destructive",
        title: "Failed to load team data",
        description: "Please refresh the page to try again",
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, toast]);

  useEffect(() => {
    if (organization?.id) {
      loadTeamData();

      // Set up polling to refresh team data every 30 seconds
      // This will catch when invitations are accepted
      const interval = setInterval(() => {
        loadTeamData();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [organization?.id, loadTeamData]);

  const handleInviteMember = async () => {
    if (!organization?.id || !userProfile?.id || !user?.email) return;

    setIsInviting(true);
    try {
      const result = await teamService.inviteTeamMember(
        organization.id,
        organization.name || "My Organization",
        inviteEmail.trim(),
        userProfile.full_name || user.email,
        user.email,
        userProfile.id,
      );

      if (result.error) throw result.error;

      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${inviteEmail}`,
      });

      setInviteEmail("");
      setIsInviteDialogOpen(false);
      loadTeamData(); // Refresh data
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to send invitation",
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (
      !confirm(`Are you sure you want to remove ${memberName} from the team?`)
    ) {
      return;
    }

    try {
      const result = await teamService.removeTeamMember(memberId);

      if (result.error) throw result.error;

      toast({
        title: "Member removed",
        description: `${memberName} has been removed from the team`,
      });

      loadTeamData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to remove member",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    }
  };

  const handleCancelInvitation = (invitationId: string, email: string) => {
    setInvitationToCancel({ id: invitationId, email });
    setCancelDialogOpen(true);
  };

  const executeCancelInvitation = async () => {
    if (!invitationToCancel) return;

    setIsCancelling(true);
    try {
      const result = await teamService.cancelInvitation(
        invitationToCancel.id,
        true, // Always delete auth user
      );

      if (result.error) throw result.error;

      toast({
        title: "Invitation cancelled",
        description:
          "The invitation has been cancelled and auth user cleaned up",
      });

      loadTeamData();
      setCancelDialogOpen(false);
      setInvitationToCancel(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to cancel invitation",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResendInvitation = async (invitation: TeamInvitation) => {
    if (!organization?.id || !userProfile?.id || !user?.email) return;

    try {
      const result = await teamService.resendInvitation(
        invitation.id,
        organization.id,
        organization.name || "My Organization",
        invitation.email,
        userProfile.full_name || user.email,
        user.email,
        userProfile.id,
      );

      if (result.error) throw result.error;

      toast({
        title: "Invitation resent",
        description: `A new invitation has been sent to ${invitation.email}`,
      });

      loadTeamData(); // Refresh data to show the new invitation
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to resend invitation",
        description:
          error instanceof Error ? error.message : "Please try again later",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (!user || !organization) {
    return (
      <SettingsContentWrapper title="Team Management">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading team information...</p>
          </div>
        </div>
      </SettingsContentWrapper>
    );
  }

  return (
    <SettingsContentWrapper
      title="Team Management"
      description="Invite team members and manage their access to your organization"
    >
      <div className="space-y-6">
        {/* Team Management Tabs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>
                  All team members have equal access to agents and settings
                </CardDescription>
              </div>
              <Dialog
                open={isInviteDialogOpen}
                onOpenChange={setIsInviteDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite Member
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>
                      Send an invitation to join your organization
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="colleague@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsInviteDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleInviteMember}
                      disabled={!inviteEmail || isInviting}
                    >
                      {isInviting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send Invitation
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="members">
                  Active Members ({teamMembers.length})
                </TabsTrigger>
                <TabsTrigger value="invitations">
                  Pending Invitations ({pendingInvitations.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="mt-6">
                <ScrollArea className="h-[400px]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : teamMembers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                      <p>No team members yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <Avatar className="h-10 w-10">
                              <AvatarImage
                                src={member.avatar_url || undefined}
                                alt={member.full_name || member.email || ""}
                              />
                              <AvatarFallback>
                                {(member.full_name || member.email || "?")
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">
                                  {member.full_name || member.email}
                                </p>
                                {member.id === userProfile?.id && (
                                  <Badge variant="outline" className="text-xs">
                                    You
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {member.email}
                              </p>
                              {member.joined_at && (
                                <p className="text-xs text-muted-foreground">
                                  Joined {formatDate(member.joined_at)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {member.id !== userProfile?.id && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() =>
                                      handleRemoveMember(
                                        member.id,
                                        member.full_name ||
                                          member.email ||
                                          "this member",
                                      )
                                    }
                                  >
                                    Remove from team
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="invitations" className="mt-6">
                <ScrollArea className="h-[400px]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : pendingInvitations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-4 opacity-20" />
                      <p>No pending invitations</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingInvitations.map((invitation) => (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-4 rounded-lg border bg-card"
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                              <Mail className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{invitation.email}</p>
                              <p className="text-sm text-muted-foreground">
                                Invited on {formatDate(invitation.created_at)}
                              </p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                <Clock className="h-3 w-3" />
                                Expires {formatDate(invitation.expires_at)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleResendInvitation(invitation)
                                  }
                                >
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Resend invitation
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() =>
                                    handleCancelInvitation(
                                      invitation.id,
                                      invitation.email,
                                    )
                                  }
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Cancel invitation
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <AlertCircle className="h-5 w-5" />
              Important Notice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-800">
              All team members have equal access to agents, conversations,
              analytics, and settings. Only invite people you trust with full
              access to your organization's data.
            </p>
          </CardContent>
        </Card>

        {/* Cancel Invitation Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel Invitation</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel the invitation for{" "}
                <span className="font-medium">{invitationToCancel?.email}</span>
                ?
              </DialogDescription>
            </DialogHeader>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> This will permanently delete their
                auth account if they haven't joined any organization yet. This
                action cannot be undone.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(false)}
                disabled={isCancelling}
              >
                Keep Invitation
              </Button>
              <Button
                variant="destructive"
                onClick={executeCancelInvitation}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cancelling...
                  </>
                ) : (
                  "Cancel Invitation"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SettingsContentWrapper>
  );
}

export default TeamSettingsContent;
